import { useTranslation } from 'react-i18next';
import type { Locale } from '@/i18n/config';
import { localeLabels, supportedLocales } from '@/i18n/config';
import { useLocale } from '@/hooks/useLocale';
import { cn } from '@/lib/utils';

interface LanguageSwitcherProps {
  className?: string;
  variant?: 'header' | 'mobile';
}

export const LanguageSwitcher = ({ className, variant = 'header' }: LanguageSwitcherProps) => {
  const { t } = useTranslation('common');
  const { locale, setLocale } = useLocale();

  return (
    <div
      className={cn(
        'flex items-center gap-1',
        variant === 'header' && 'text-sm font-light uppercase tracking-wider',
        variant === 'mobile' && 'text-base font-light uppercase tracking-wider',
        className,
      )}
      role="group"
      aria-label={t('languageSwitcher.label')}
    >
      {supportedLocales.map((lng, index) => (
        <span key={lng} className="flex items-center gap-1">
          {index > 0 && <span className="text-muted-foreground/50" aria-hidden="true">|</span>}
          <button
            type="button"
            onClick={() => setLocale(lng as Locale)}
            className={cn(
              'focus-ring rounded-sm transition-colors hover:text-foreground',
              locale === lng ? 'text-foreground font-medium' : 'text-muted-foreground',
            )}
            aria-current={locale === lng ? 'true' : undefined}
            aria-label={t(`languageSwitcher.${lng}`)}
          >
            {localeLabels[lng]}
          </button>
        </span>
      ))}
    </div>
  );
};
